import { DEFAULT_BOARD_ID, EXAM_BOARDS } from "./boards";
import type { ExamBoardConfig, ExamBoardId, ExamSitting } from "./types";

/** apex hostname (e.g. "tmua.fyi") -> board id, built from each board's domains. */
const HOST_TO_BOARD: Record<string, ExamBoardId> = Object.fromEntries(
  (Object.values(EXAM_BOARDS) as ExamBoardConfig[]).flatMap((board) =>
    board.domains.map((domain) => [domain.toLowerCase(), board.id] as const),
  ),
);

/** Strip port, lowercase, and drop common subdomains down to the apex. */
function normalizeHost(host: string): string {
  const bare = host.toLowerCase().trim().split(":")[0];
  const labels = bare.split(".").filter(Boolean);
  // Drop leading www/dev/staging/preview so "dev.tmua.fyi" -> "tmua.fyi".
  while (
    labels.length > 2 &&
    ["www", "dev", "staging", "preview", "app", "dashboard"].includes(labels[0])
  ) {
    labels.shift();
  }
  return labels.join(".");
}

/**
 * Resolve an exam-board id from a request hostname.
 *
 * Resolution order:
 *   1. explicit override (env / query) via `override`
 *   2. exact apex-host match against board domains
 *   3. DEFAULT_BOARD_ID
 */
export function resolveBoardId(
  host?: string | null,
  override?: string | null,
): ExamBoardId {
  if (override) {
    const up = override.toUpperCase();
    if (up in EXAM_BOARDS) return up as ExamBoardId;
  }
  if (host) {
    const apex = normalizeHost(host);
    const matched = HOST_TO_BOARD[apex];
    if (matched) return matched;
  }
  return DEFAULT_BOARD_ID;
}

/** Resolve the full board config from a request hostname. */
export function resolveBoard(
  host?: string | null,
  override?: string | null,
): ExamBoardConfig {
  return EXAM_BOARDS[resolveBoardId(host, override)];
}

/**
 * The sitting a user is preparing for, from their onboarding answer
 * ("October sitting" / "January sitting"). Falls back to the soonest sitting.
 */
export function resolveSitting(
  board: ExamBoardConfig,
  answer: string | null | undefined,
): ExamSitting | undefined {
  const sittings = board.sittings;
  if (!sittings?.length) return undefined;
  const a = (answer ?? "").toLowerCase();
  const key = a.includes("january")
    ? "january"
    : a.includes("october")
      ? "october"
      : undefined;
  return (key && sittings.find((s) => s.key === key)) || sittings[0];
}
